import { graphql } from "babel-plugin-relay/macro";
import { useFragment, useMutation } from "react-relay";
import { useEffect, useState } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { TextWithTooltip } from "../../components/TextWithTooltip";
import styles from "./selfServiceSettings.module.scss";
import { selfServiceSettingsSaveMutation } from "./__generated__/selfServiceSettingsSaveMutation.graphql";
import { selfServiceSettingsResetDomainLockMutation } from "./__generated__/selfServiceSettingsResetDomainLockMutation.graphql";
import { selfServiceSettings_mainSettingsFragment$key } from "./__generated__/selfServiceSettings_mainSettingsFragment.graphql";

const URL_HIT_JOURNEY_TYPE = "URL_HIT";
const END_TO_END_JOURNEY_TYPE = "END_TO_END";

const SelfServiceSettingsMainSettingsFragment = graphql`
    fragment selfServiceSettings_mainSettingsFragment on UserJourney {
        id
        name
        cutoff
        cutOffOK
        alertOnSlow
        journeyScribeStartingDomain
        journeyScribeStartingDomainLocked
        journeyScribeStartingURL
        journeyScribeJourneyType
    }
`;

export function SelfServiceSettingsForm({
    ujID,
    userJourney,
    isAdmin,
}: {
    ujID: number;
    userJourney: selfServiceSettings_mainSettingsFragment$key;
    isAdmin: boolean;
}) {
    const userJourneySettings = useFragment(
        SelfServiceSettingsMainSettingsFragment,
        userJourney,
    );

    const [saveSettings, isSavingSettings] =
        useMutation<selfServiceSettingsSaveMutation>(graphql`
            mutation selfServiceSettingsSaveMutation(
                $ujID: Int!
                $name: String!
                $cutoff: Int
                $cutOffOK: Boolean!
                $alertOnSlow: Boolean!
                $startingDomain: String
                $startingURL: String
            ) {
                setSelfServiceUJSettings(
                    ujData: {
                        id: $ujID
                        name: $name
                        cutoff: $cutoff
                        cutOffOK: $cutOffOK
                        alertOnSlow: $alertOnSlow
                        startingDomain: $startingDomain
                        startingURL: $startingURL
                    }
                ) {
                    userJourney {
                        ...selfServiceSettings_mainSettingsFragment
                    }
                }
            }
        `);

    const [resetDomainLock, isResettingDomainLock] =
        useMutation<selfServiceSettingsResetDomainLockMutation>(graphql`
            mutation selfServiceSettingsResetDomainLockMutation($ujID: Int!) {
                resetJourneyScribeDomainLock(ujID: $ujID) {
                    userJourney {
                        ...selfServiceSettings_mainSettingsFragment
                    }
                }
            }
        `);

    const isDomainLocked =
        userJourneySettings.journeyScribeStartingDomainLocked;

    const [formData, setFormData] = useState({
        name: userJourneySettings.name,
        cutoff: userJourneySettings.cutoff,
        cutOffOK: userJourneySettings.cutOffOK,
        alertOnSlow: userJourneySettings.alertOnSlow,
        startingDomain: userJourneySettings.journeyScribeStartingDomain ?? "",
        startingURL: userJourneySettings.journeyScribeStartingURL ?? "",
    });

    useEffect(() => {
        setFormData({
            name: userJourneySettings.name,
            cutoff: userJourneySettings.cutoff,
            cutOffOK: userJourneySettings.cutOffOK,
            alertOnSlow: userJourneySettings.alertOnSlow,
            startingDomain:
                userJourneySettings.journeyScribeStartingDomain ?? "",
            startingURL: userJourneySettings.journeyScribeStartingURL ?? "",
        });
    }, [
        userJourneySettings.name,
        userJourneySettings.cutoff,
        userJourneySettings.cutOffOK,
        userJourneySettings.alertOnSlow,
        userJourneySettings.journeyScribeStartingDomain,
        userJourneySettings.journeyScribeStartingURL,
    ]);

    useEffect(() => {
        setValidationError("");
        setShowSuccess(false);
    }, [ujID]);

    const [showSuccess, setShowSuccess] = useState(false);
    const [validationError, setValidationError] = useState("");

    const handleSave = () => {
        if (formData.cutoff && formData.cutoff < 0) {
            setValidationError("Cutoff must be a positive integer.");
            return;
        }

        saveSettings({
            variables: {
                ujID: ujID,
                name: formData.name,
                cutoff: formData.cutoff,
                cutOffOK: formData.cutOffOK ?? false,
                alertOnSlow: formData.alertOnSlow,
                startingDomain: formData.startingDomain || null,
                startingURL: formData.startingURL || null,
            },
            onCompleted(_, errors) {
                if (errors && errors.length > 0) {
                    setValidationError(
                        errors[0].message ||
                            "Failed to save settings, please try again.",
                    );
                } else {
                    setShowSuccess(true);
                }
            },
            onError(error) {
                setValidationError(
                    error.message ||
                        "Failed to save settings, please try again.",
                );
            },
        });
    };

    const handleResetDomainLock = () => {
        resetDomainLock({
            variables: { ujID },
            onCompleted(_, errors) {
                if (errors && errors.length > 0) {
                    setValidationError(
                        errors[0].message ||
                            "Failed to reset domain/URL lock, please try again.",
                    );
                } else {
                    setShowSuccess(true);
                }
            },
            onError(error) {
                setValidationError(
                    error.message ||
                        "Failed to reset domain/URL lock, please try again.",
                );
            },
        });
    };

    return (
        <Form className={styles.formContainer}>
            <Form.Group className={styles.formGroup}>
                <Form.Label htmlFor="ujName">User Journey Name</Form.Label>
                <Form.Control
                    type="text"
                    id="ujName"
                    value={formData.name}
                    maxLength={40}
                    onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                    }
                />
            </Form.Group>

            <Form.Group className={styles.formGroup}>
                <Form.Label htmlFor="cutoff">
                    <TextWithTooltip name={"Cutoff"} />
                </Form.Label>
                <Form.Control
                    id="cutoff"
                    type="number"
                    min={1}
                    step={1}
                    value={formData.cutoff ?? ""}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            cutoff: e.target.value
                                ? parseInt(e.target.value, 10)
                                : null,
                        })
                    }
                />
            </Form.Group>

            <Form.Group className={styles.formGroup}>
                <div className="form-check form-switch">
                    <Form.Check
                        type="switch"
                        id="cutOffOK-switch"
                        label={<TextWithTooltip name={"Cutoff OK"} />}
                        checked={formData.cutOffOK ?? false}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                cutOffOK: e.target.checked,
                            })
                        }
                    />
                </div>
            </Form.Group>

            <Form.Group className={styles.formGroup}>
                <div className="form-check form-switch">
                    <Form.Check
                        type="switch"
                        id="alertOnSlow-switch"
                        label={<TextWithTooltip name={"Alert On Slow"} />}
                        checked={formData.alertOnSlow}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                alertOnSlow: e.target.checked,
                            })
                        }
                    />
                </div>
            </Form.Group>

            {userJourneySettings.journeyScribeJourneyType ===
                URL_HIT_JOURNEY_TYPE && (
                <Form.Group className={styles.formGroup}>
                    <Form.Label htmlFor="startingDomain">
                        <TextWithTooltip name={"Starting Domain"} />
                    </Form.Label>
                    <Form.Control
                        type="text"
                        id="startingDomain"
                        value={formData.startingDomain}
                        maxLength={253}
                        readOnly={isDomainLocked}
                        disabled={isDomainLocked}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                startingDomain: e.target.value,
                            })
                        }
                    />
                    {isDomainLocked && (
                        <Form.Text className="text-muted">
                            The starting domain is locked and cannot be changed.
                        </Form.Text>
                    )}
                </Form.Group>
            )}

            {userJourneySettings.journeyScribeJourneyType ===
                END_TO_END_JOURNEY_TYPE && (
                <Form.Group className={styles.formGroup}>
                    <Form.Label htmlFor="startingURL">
                        <TextWithTooltip name={"Starting URL"} />
                    </Form.Label>
                    <Form.Control
                        type="text"
                        id="startingURL"
                        value={formData.startingURL}
                        maxLength={2000}
                        readOnly={isDomainLocked}
                        disabled={isDomainLocked}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                startingURL: e.target.value,
                            })
                        }
                    />
                    {isDomainLocked && (
                        <Form.Text className="text-muted">
                            The starting URL is locked and cannot be changed.
                        </Form.Text>
                    )}
                </Form.Group>
            )}

            {isAdmin && isDomainLocked && (
                <Button
                    variant="outline-warning"
                    className={styles.saveButton}
                    onClick={handleResetDomainLock}
                    disabled={isResettingDomainLock}
                >
                    {userJourneySettings.journeyScribeJourneyType ===
                    END_TO_END_JOURNEY_TYPE
                        ? "Reset URL Lock"
                        : "Reset Domain Lock"}
                </Button>
            )}

            <Button
                variant="tribe-primary"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={isSavingSettings}
            >
                Save Changes
            </Button>

            {showSuccess && (
                <SuccessToast clearToast={() => setShowSuccess(false)} />
            )}

            {validationError && (
                <ErrorToast
                    errorMessage={validationError}
                    clearToast={() => setValidationError("")}
                />
            )}
        </Form>
    );
}

interface ToastProps {
    clearToast: () => void;
    successMessage?: string;
}

export function SuccessToast({ clearToast, successMessage }: ToastProps) {
    return (
        <Alert
            variant="success"
            className={styles.toastMessage}
            onClose={() => clearToast()}
            dismissible
        >
            {successMessage ?? "Settings saved successfully!"}
        </Alert>
    );
}

interface ErrorToastProps extends ToastProps {
    errorMessage: string;
}

export function ErrorToast({ clearToast, errorMessage }: ErrorToastProps) {
    return (
        <Alert
            variant="danger"
            className={styles.toastMessage}
            onClose={() => clearToast()}
            dismissible
        >
            {errorMessage}
        </Alert>
    );
}
